// Multi-select on the map (managers; `canSelectOnMap`): the pure side of choosing stores by pin, by box and "Select
// all shown", and of saving a bulk edit in chunks with progress. No React and no MapLibre, so it runs in unit tests.
//
// The selection is a set of lower-case ids. It can hold stores the current filter hides (a filter changed after the
// pick); what counts, what is highlighted and what an assignment changes is always the selection intersected with
// the stores on the map now (`selectedStores`), so a filter never assigns stores the person cannot see.

import type { RecordPayload } from '@/contracts/models';
import { formatGrouped } from '@/domain/format';
import { isValidCoordinate } from '@/domain/geo';
import { idKey } from '@/domain/util';
import { inViewBounds, type ViewBounds } from './mapViewModel';

export type Selection = ReadonlySet<string>;

export const EMPTY_SELECTION: Selection = new Set<string>();

/** Adds or removes one store (any id case). */
export function toggleSelected(selection: Selection, id: string): Selection {
  const next = new Set(selection);
  const key = idKey(id);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** Adds stores to the selection (a box adds to what is already picked). Returns the same set when nothing is new. */
export function addToSelection(selection: Selection, ids: Iterable<string>): Selection {
  let next: Set<string> | null = null;
  for (const id of ids) {
    const key = idKey(id);
    if ((next ?? selection).has(key)) continue;
    next ??= new Set(selection);
    next.add(key);
  }
  return next ?? selection;
}

/**
 * The stores on the map inside a drawn box (west, south, east, north as MapLibre reports it): every located store
 * that passes the filters, clustered or not, so a box over a cluster bubble takes all of its stores.
 */
export function storesInBox<R extends RecordPayload>(located: readonly R[], box: ViewBounds): R[] {
  return located.filter((r) => isValidCoordinate(r.coordinate) && inViewBounds(r.coordinate, box));
}

/** "Select all": every store on the map with the current filters (not only the pins rendered right now). */
export function allShown(located: readonly RecordPayload[]): Selection {
  const out = new Set<string>();
  for (const r of located) out.add(idKey(r.id));
  return out;
}

/** The selected stores that are on the map now, in map order. */
export function selectedStores<R extends RecordPayload>(located: readonly R[], selection: Selection): R[] {
  if (selection.size === 0) return [];
  return located.filter((r) => selection.has(idKey(r.id)));
}

/**
 * Escape in select mode (with no dialog, menu or field taking it) always drops the whole selection, picks the filter
 * hides included; it also leaves select mode when nothing was picked on the map. So the first Escape clears what can
 * be seen, the next one leaves, and, as with the Select button, coming back never brings back an old selection.
 */
export function escapeLeavesSelectMode(selectedOnMap: number): boolean {
  return selectedOnMap === 0;
}

/**
 * Where the map pans when a Stores-list row is toggled in select mode: the store's spot when it is outside the view,
 * else nowhere (null). The zoom stays put, so picking many stores from the list keeps the region in sight.
 */
export function listPickPanTarget(
  record: Pick<RecordPayload, 'coordinate'> | undefined,
  view: ViewBounds | null,
): { latitude: number; longitude: number } | null {
  if (!record || !isValidCoordinate(record.coordinate) || !view) return null;
  return inViewBounds(record.coordinate, view) ? null : record.coordinate;
}

/** "12 selected" / "1,240 selected". */
export function selectedText(count: number): string {
  return `${formatGrouped(count)} selected`;
}

/** "Select all 4,712": the count is in the label because it takes every store the filters let through, in view or not. */
export function selectAllText(shownCount: number): string {
  return `Select all ${formatGrouped(shownCount)}`;
}

/** "1 store" / "4,712 stores". */
export function storeCountText(count: number): string {
  return `${formatGrouped(count)} ${count === 1 ? 'store' : 'stores'}`;
}

/**
 * The success toast after a bulk assign: "Assigned 12 stores to Jane Doe", or, for Unassigned, "Removed the owner
 * from 12 stores".
 */
export function assignedText(count: number, ownerName: string | null): string {
  return ownerName === null ? `Removed the owner from ${storeCountText(count)}` : `Assigned ${storeCountText(count)} to ${ownerName}`;
}

export interface ChunkedSaveResult {
  /** Patches applied (sent to the outbox). */
  applied: number;
  /** Patches the replica refused because the store is gone (deleted elsewhere) or being deleted. */
  skipped: number;
}

/**
 * Runs `save` over `chunks` one at a time, reporting progress after each and yielding in between so the progress bar
 * paints and the page stays responsive on a 5,000-store assign. Each chunk is all-or-nothing (`engine.save`); if one
 * throws, the chunks before it stay saved and the caller gets a `ChunkedSaveError` saying how many were.
 */
export async function saveInChunks<P>(
  chunks: readonly (readonly P[])[],
  save: (patches: readonly P[]) => { skipped: readonly unknown[] },
  options: { onProgress?: (done: number, total: number) => void; pause?: () => Promise<void> } = {},
): Promise<ChunkedSaveResult> {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const pause = options.pause ?? nextFrame;
  let done = 0;
  let skipped = 0;
  options.onProgress?.(0, total);
  for (const chunk of chunks) {
    let result: { skipped: readonly unknown[] };
    try {
      result = save(chunk);
    } catch (error) {
      throw new ChunkedSaveError(error, done - skipped, total);
    }
    done += chunk.length;
    skipped += result.skipped.length;
    options.onProgress?.(done, total);
    await pause();
  }
  return { applied: done - skipped, skipped };
}

/** A chunk failed part way through a bulk save; `applied` patches before it were saved. */
export class ChunkedSaveError extends Error {
  readonly applied: number;
  readonly total: number;
  constructor(cause: unknown, applied: number, total: number) {
    super(cause instanceof Error && cause.message ? cause.message : 'The changes could not be saved.', { cause });
    this.name = 'ChunkedSaveError';
    this.applied = applied;
    this.total = total;
  }
}

/** Waits for the next paint (a timer where there is no requestAnimationFrame, e.g. a background tab or tests). */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function' && typeof document !== 'undefined' && document.visibilityState === 'visible') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });
}
